import { Link } from 'react-router-dom';
import { usePrivy } from '@privy-io/react-auth';
import { useQuery } from '@tanstack/react-query';
import { Bell, LogOut, Settings, Shield, User, Wallet } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { formatAddress } from '@/lib/utils';
import { api } from '@/lib/api';
import { useAuthStore } from '@/stores/auth.store';
import { useUSDCBalance } from '@/hooks/useEscrow';
import { WalletPanel } from './WalletPanel';

export function Header() {
  const { user: privyUser, logout } = usePrivy();
  const appUser = useAuthStore((s) => s.user);
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated);

  const { data: unreadCount = 0 } = useQuery({
    queryKey: ['notifications', 'unread-count'],
    queryFn: () => api.getUnreadNotificationCount(),
    enabled: isAuthenticated,
    refetchInterval: 30_000,
  });

  const { balance: usdcBalance } = useUSDCBalance();
  const formattedBalance = usdcBalance != null ? `$${parseFloat(usdcBalance).toFixed(2)}` : null;

  const displayName =
    appUser?.displayName ||
    privyUser?.email?.address ||
    (privyUser?.wallet?.address ? formatAddress(privyUser.wallet.address) : 'User');

  return (
    <header className="sticky top-0 z-50 w-full border-b bg-background/80 backdrop-blur-xl">
      <div className="flex h-16 items-center justify-between px-4 lg:px-8">
        <Link to="/dashboard" className="flex items-center gap-2">
          <div className="flex h-8 w-8 items-center justify-center rounded-xl bg-gradient-to-br from-violet-600 to-cyan-500">
            <span className="font-heading text-lg font-bold text-white">P</span>
          </div>
          <span className="hidden font-heading text-xl font-bold sm:inline">PullUp</span>
        </Link>

        <div className="flex items-center gap-2">
          <Link to="/notifications">
            <Button variant="ghost" size="icon" className="relative">
              <Bell className="h-5 w-5" />
              {unreadCount > 0 && (
                <span className="absolute right-1 top-1 h-2 w-2 animate-pulse rounded-full bg-primary shadow-[0_0_8px_hsl(var(--primary)/0.5)]" />
              )}
            </Button>
          </Link>

          {/* Wallet dropdown */}
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" size="sm" className="gap-1.5 px-2">
                <Wallet className="h-4 w-4" />
                {formattedBalance && (
                  <span className="text-xs font-medium text-muted-foreground">{formattedBalance}</span>
                )}
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="p-0">
              <WalletPanel />
            </DropdownMenuContent>
          </DropdownMenu>

          {/* User menu dropdown */}
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" className="gap-2 pl-2 pr-3">
                <Avatar className="h-8 w-8">
                  <AvatarImage src={appUser?.avatarUrl || (privyUser?.google as { picture?: string } | undefined)?.picture} />
                  <AvatarFallback className="bg-primary text-primary-foreground">
                    {displayName[0].toUpperCase()}
                  </AvatarFallback>
                </Avatar>
                <span className="hidden text-sm font-medium sm:inline">{displayName}</span>
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-56">
              <DropdownMenuItem asChild>
                <Link to={`/profile/${appUser?.id || ''}`} className="flex items-center gap-2">
                  <User className="h-4 w-4" />
                  Profile
                </Link>
              </DropdownMenuItem>
              <DropdownMenuItem asChild>
                <Link to="/settings" className="flex items-center gap-2">
                  <Settings className="h-4 w-4" />
                  Settings
                </Link>
              </DropdownMenuItem>
              {(appUser?.role === 'admin' || appUser?.role === 'super_admin') && (
                <>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem asChild>
                    <Link to="/admin" className="flex items-center gap-2">
                      <Shield className="h-4 w-4" />
                      Admin Dashboard
                    </Link>
                  </DropdownMenuItem>
                </>
              )}
              <DropdownMenuSeparator />
              <DropdownMenuItem onClick={logout} className="text-destructive">
                <LogOut className="mr-2 h-4 w-4" />
                Log out
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>
    </header>
  );
}
